import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { SlidersHorizontal } from "lucide-react";

const Shop = () => {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [cats, setCats] = useState([]);
  const [brands, setBrands] = useState([]);
  const [bogoCoupons, setBogoCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const cat = params.get("category") || "";
  const brand = params.get("brand") || "";
  const q = params.get("q") || "";
  const sort = params.get("sort") || "";
  const minP = params.get("min_price") || "";
  const maxP = params.get("max_price") || "";
  const isCategoryBrowse = Boolean(cat) && !brand && !q && !minP && !maxP;

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data));
    api.get("/brands").then((r) => setBrands(r.data));
    api.get("/coupons").then((r) => {
      setBogoCoupons(r.data.filter((coupon) => coupon.kind === "bogo"));
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (cat && !isCategoryBrowse) qs.set("category", cat);
    if (brand) qs.set("brand", brand);
    if (q) qs.set("q", q);
    if (sort) qs.set("sort", sort);
    if (minP) qs.set("min_price", minP);
    if (maxP) qs.set("max_price", maxP);
    if (isCategoryBrowse) qs.set("limit", "200");
    api.get(`/products?${qs.toString()}`).then((r) => {
      setProducts(r.data.items);
      setTotal(r.data.total);
      setLoading(false);
    });
  }, [cat, brand, q, sort, minP, maxP, isCategoryBrowse]);

  const setParam = (k, v) => {
    const n = new URLSearchParams(params);
    if (v) n.set(k, v);
    else n.delete(k);
    setParams(n);
  };

  const activeCat = cats.find((c) => c.slug === cat);
  const bogoOffersByProduct = useMemo(() => {
    const offers = new Map();
    bogoCoupons.forEach((coupon) => {
      coupon.product_ids?.forEach((productId) => {
        if (!offers.has(productId)) offers.set(productId, coupon);
      });
    });
    return offers;
  }, [bogoCoupons]);
  const orderedCategories = useMemo(
    () => [...cats].sort((left, right) => {
      const sequence = Number(left.order || 0) - Number(right.order || 0);
      return sequence || left.name.localeCompare(right.name);
    }),
    [cats],
  );
  const categorySections = useMemo(() => {
    if (!isCategoryBrowse) return [];
    const sections = orderedCategories.map((category) => ({
      category,
      products: products.filter((product) => product.category_id === category.id),
    }));
    const selectedSection = sections.find((section) => section.category.slug === cat);
    const remainingSections = sections.filter((section) => section.category.slug !== cat);
    return [selectedSection, ...remainingSections]
      .filter(Boolean)
      .filter((section) => section.category.slug === cat || section.products.length > 0);
  }, [cat, isCategoryBrowse, orderedCategories, products]);
  const activeCategoryProductCount = categorySections.find(
    (section) => section.category.slug === cat,
  )?.products.length || 0;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-8" data-testid="shop-page">
      <div className="mb-8">
        <div className="text-xs tracking-[0.25em] uppercase text-[#8C6D46] mb-2">
          {q ? "Search results" : activeCat ? "Category" : "All products"}
        </div>
        <h1 className="font-serif text-4xl lg:text-5xl font-semibold text-[#1E2A24]">
          {q ? `"${q}"` : activeCat ? activeCat.name : "The Eira Collection"}
        </h1>
        <p className="text-[#64748B] mt-2">
          {isCategoryBrowse && activeCat ? activeCategoryProductCount : total} products
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        <button onClick={() => setShowFilters(!showFilters)} className="lg:hidden inline-flex items-center gap-2 bg-white border border-[#E2DCD2] rounded-lg px-4 py-2 text-sm w-fit">
          <SlidersHorizontal size={16} /> Filters
        </button>

        <aside className={`${showFilters ? "block" : "hidden"} lg:block w-full lg:w-64 space-y-6 shrink-0`} data-testid="filters-panel">
          <div>
            <div className="font-semibold text-sm mb-3">Category</div>
            <div className="space-y-2">
              <button onClick={() => setParam("category", "")} className={`block w-full text-left text-sm py-1 ${!cat ? "text-[#C86D42] font-semibold" : "text-[#1E2A24]"}`}>All</button>
              {cats.map((c) => (
                <button key={c.id} onClick={() => setParam("category", c.slug)} className={`block w-full text-left text-sm py-1 ${cat === c.slug ? "text-[#C86D42] font-semibold" : "text-[#1E2A24]"}`} data-testid={`filter-cat-${c.slug}`}>
                  {c.name}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="font-semibold text-sm mb-3">Brand</div>
            <div className="space-y-2">
              <button onClick={() => setParam("brand", "")} className={`block w-full text-left text-sm py-1 ${!brand ? "text-[#C86D42] font-semibold" : "text-[#1E2A24]"}`}>All brands</button>
              {brands.map((b) => (
                <button key={b.id} onClick={() => setParam("brand", b.slug)} className={`block w-full text-left text-sm py-1 ${brand === b.slug ? "text-[#C86D42] font-semibold" : "text-[#1E2A24]"}`} data-testid={`filter-brand-${b.slug}`}>
                  {b.name}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="font-semibold text-sm mb-3">Price Range</div>
            <div className="flex gap-2">
              <input type="number" placeholder="Min" value={minP} onChange={(e) => setParam("min_price", e.target.value)} className="w-full bg-white border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm outline-none" />
              <input type="number" placeholder="Max" value={maxP} onChange={(e) => setParam("max_price", e.target.value)} className="w-full bg-white border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm outline-none" />
            </div>
          </div>
          <button onClick={() => setParams({})} className="text-sm text-[#C86D42] font-medium">Clear all filters</button>
        </aside>

        <div className="flex-1">
          <div className="flex items-center justify-between mb-6">
            <div className="text-sm text-[#64748B]">
              {isCategoryBrowse ? "Category collection" : `Showing ${products.length} of ${total}`}
            </div>
            <select value={sort} onChange={(e) => setParam("sort", e.target.value)} className="bg-white border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm" data-testid="sort-select">
              <option value="">Featured</option>
              <option value="price_asc">Price: Low to High</option>
              <option value="price_desc">Price: High to Low</option>
              <option value="rating">Customer Rating</option>
              <option value="best_selling">Best Selling</option>
            </select>
          </div>
          {loading ? (
            <div className="text-center py-20 text-[#64748B]">Loading…</div>
          ) : isCategoryBrowse && categorySections.length > 0 ? (
            <div className="space-y-14" data-testid="shop-category-sections">
              {categorySections.map((section) => {
                const isSelectedCategory = section.category.slug === cat;
                const sectionBogoOffers = [...new Map(
                  section.products
                    .map((product) => bogoOffersByProduct.get(product.id))
                    .filter(Boolean)
                    .map((coupon) => [coupon.id, coupon]),
                ).values()];
                return (
                  <section
                    key={section.category.id}
                    className="border-t border-[#E2DCD2] pt-6 first:border-t-0 first:pt-0"
                    data-testid={`shop-category-section-${section.category.slug}`}
                  >
                    <div className="mb-5 flex items-end justify-between gap-4">
                      <div>
                        <p className="text-xs uppercase tracking-[0.2em] text-[#8C6D46]">
                          {isSelectedCategory ? "Selected category" : "More to explore"}
                        </p>
                        <h2 className="mt-1 font-serif text-3xl font-semibold text-[#1E2A24]">
                          {section.category.name}
                        </h2>
                      </div>
                      <span className="text-sm text-[#64748B]">
                        {section.products.length} items
                      </span>
                    </div>
                    {isSelectedCategory && sectionBogoOffers.length > 0 && (
                      <div
                        className="mb-5 border-l-4 border-[#2D5A47] bg-[#EBF2EE] px-4 py-3 text-sm text-[#1E2A24]"
                        data-testid="selected-category-bogo-offers"
                      >
                        <span className="font-semibold">Buy 1 Get 1 Free</span>
                        <span className="ml-2 text-[#64748B]">
                          Eligible products are marked below. Use code {sectionBogoOffers[0].code}.
                        </span>
                      </div>
                    )}
                    {section.products.length ? (
                      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:gap-6">
                        {section.products.map((product) => (
                          <ProductCard
                            key={product.id}
                            product={product}
                            bogoOffer={bogoOffersByProduct.get(product.id)}
                          />
                        ))}
                      </div>
                    ) : (
                      <p className="py-6 text-sm text-[#64748B]">
                        No products are available in this category yet.
                      </p>
                    )}
                  </section>
                );
              })}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-20 text-[#64748B]">No products found.</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 lg:gap-6">
              {products.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  bogoOffer={bogoOffersByProduct.get(product.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Shop;
